import { isExactCodeMatch } from '../utils/exactCodeMatch';
import ApiWithLogging from '../utils/apiWithLogging';

// ประเภทผู้ติดต่อ
export enum ContactType {
  SECURITY = 'SECURITY',       // รปภ.
  JURISTIC = 'JURISTIC',       // นิติบุคคล
  MANAGER = 'MANAGER',         // ผู้จัดการ
  TECHNICIAN = 'TECHNICIAN',   // ช่างเทคนิค
  OTHER = 'OTHER'              // อื่นๆ
}

// Contact Type labels (Thai)
export const ContactTypeLabels: Record<ContactType, string> = {
  [ContactType.SECURITY]: 'รปภ.',
  [ContactType.JURISTIC]: 'นิติบุคคล',
  [ContactType.MANAGER]: 'ผู้จัดการ',
  [ContactType.TECHNICIAN]: 'ช่างเทคนิค',
  [ContactType.OTHER]: 'อื่นๆ'
};

// ข้อมูลผู้ติดต่อของโครงการ
export interface SiteContact {
  id?: number;
  site_id?: number;
  contact_type: ContactType;
  name?: string;
  phone?: string;
  line_id?: string;
  notes?: string;
  is_primary: boolean;
  created_at?: string;
}

// ข้อมูลผู้ติดต่อสำหรับ form
export interface SiteContactForm {
  contact_type: ContactType;
  name?: string;
  phone?: string;
  line_id?: string;
  notes?: string;
  is_primary?: boolean;
}

// ประเภทข้อมูลโครงการ
export interface Site {
  id: number;
  code: string;
  name: string;
  display_name?: string;  // ชื่อใช้งานจริงในระบบ
  app_name?: string;  // ชื่อที่ใช้ในแอพ
  company_id: number;
  company_name: string;  // ชื่อบริษัทที่โครงการสังกัด (จาก backend)
  company?: {         // ข้อมูลบริษัท (จาก relation)
    id: number;
    name: string;
  };
  address: string;
  customer_code?: string;  // legacy — K-Visit ใช้จับคู่โครงการ (ฟอร์มไม่เขียนทับแล้ว)
  customer_number?: string; // รหัสลูกค้า (ตรงกับคอลัมน์ "รหัสลูกค้า" ในไฟล์นำเข้า)
  activation_code?: string; // รหัส Activate สำหรับเปิดใช้งาน
  contactPerson: string;
  phone: string;
  email: string;
  status: string;  // เพิ่ม status field

  // New fields for data migration
  k_connect_enabled?: boolean;  // ใช้งาน K-Connect
  telegram_enabled?: boolean;   // เปิดใช้ Telegram notification
  project_phone?: string;       // เบอร์โทรโครงการ/นิติบุคคล
  contact_notes?: string;       // ข้อมูลติดต่อ รปภ./ผู้ดูแล

  // Site contacts (embedded)
  contacts?: SiteContact[];

  // Phase 2 สถานะโครงการ: aggregate จาก backend (GET /sites/ + /sites/{id})
  active_rental_count?: number;   // จำนวนการเช่าที่ยังใช้งาน (ACTIVE/PENDING_CANCELLATION)
  active_contract_count?: number; // จำนวนสัญญา ACTIVE
  // E2E v6 OBS-4c: ร่างการเช่า (DRAFT) — ไม่นับใน active แต่ระบบยังถือว่าโครงการใช้งานอยู่
  // (กฎเสนอปิดโครงการ / ปิดโครงการเดิมตอนย้ายอุปกรณ์)
  draft_rental_count?: number;

  created_at: string;
  updated_at?: string;
  created_by?: {     // ผู้สร้าง
    id: number;
    username: string;
    full_name?: string;
  };
  updated_by?: {     // ผู้แก้ไขล่าสุด
    id: number;
    username: string;
    full_name?: string;
  };
}

/** อุปกรณ์/ซิมที่อยู่ที่โครงการตอนนี้ — GET /sites/{id}/current-equipment (28 ต.ค. 2026)
 *  จาก rental_items ของสัญญา ACTIVE/PENDING_CANCELLATION · AnyDesk จาก rental_items เท่านั้น */
export interface SiteCurrentEquipmentItem {
  rental_item_id: number;
  rental_id: number;
  rental_number: string | null;
  rental_status: string | null;
  /** ว่าง = สัญญายังไม่ส่งมอบ */
  delivery_date: string | null;
  product_name: string | null;
  quantity: number | null;
  device: { id: number; code: string | null; serial_number: string | null; name: string | null; is_serialized: boolean } | null;
  sim: { id: number; phone_number: string | null; operator: string | null } | null;
  anydesk_id: string | null;
  /** รับคืนก่อนสัญญาจบแล้ว (ใบตามเก็บ COMPLETED) — สัญญายังถืออยู่ */
  device_returned: boolean;
  sim_returned: boolean;
}

// ข้อมูลสำหรับการสร้างโครงการใหม่หรือแก้ไขโครงการ
export interface SiteForm {
  code: string;
  name: string;
  display_name?: string;  // ชื่อใช้งานจริงในระบบ
  app_name?: string;  // ชื่อที่ใช้ในแอพ
  company_id: number;
  address: string;
  customer_code?: string;  // legacy — K-Visit ใช้จับคู่โครงการ (ฟอร์มไม่เขียนทับแล้ว)
  customer_number?: string; // รหัสลูกค้า (ตรงกับคอลัมน์ "รหัสลูกค้า" ในไฟล์นำเข้า)
  activation_code?: string; // รหัส Activate สำหรับเปิดใช้งาน
  contactPerson: string;
  phone: string;
  email: string;
  status: string;  // เพิ่ม status field

  // New fields for data migration
  k_connect_enabled?: boolean;
  telegram_enabled?: boolean;
  project_phone?: string;
  contact_notes?: string;

  // Site contacts
  contacts?: SiteContactForm[];

  // E2E v6 OBS-3: ยืนยันปิดโครงการที่ยังมีการเช่า/สัญญาใช้งานอยู่ (ส่งเฉพาะตอน update
  // หลังผู้ใช้กดยืนยัน — ไม่ส่ง = backend ตอบ 409 SITE_HAS_ACTIVE_USAGE ถ้ายังมีการใช้งาน)
  confirm_deactivate?: boolean;
}

// พารามิเตอร์สำหรับการดึงข้อมูลโครงการ
export interface GetSitesParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  company_id?: number;
  status?: string;
}

// การตอบกลับจากการดึงข้อมูลโครงการ
export interface GetSitesResponse {
  items: Site[];
  total: number;
  page: number;
  limit: number;
}

// บริการจัดการข้อมูลโครงการ
// บัญชีเข้าระบบแอพของโครงการ (PO 30 ก.ย. 2026) — endpoint แยกจากข้อมูลโครงการ รหัสผ่านไม่มากับ list/detail
export interface SiteAppCredentialsMeta {
  site_id: number;
  app_login_email: string | null;
  has_password: boolean;
  /** มีสิทธิ์ sites.app_credentials (ดูรหัสจริง/ตั้ง/ล้าง) */
  can_manage: boolean;
}

export interface SiteAppCredentialsUpdate {
  /** ไม่ส่ง key = ไม่แตะ · null/'' = ล้าง */
  app_login_email?: string | null;
  /** รหัสใหม่ (ห้ามว่าง) */
  password?: string;
  clear_password?: boolean;
}

const siteService = {
  // อุปกรณ์/ซิมที่อยู่ที่โครงการตอนนี้ (sites.view) — การ์ดในหน้ารายละเอียดโครงการ
  async getCurrentEquipment(siteId: number): Promise<{ site_id: number; items: SiteCurrentEquipmentItem[]; total: number }> {
    return ApiWithLogging.get(`/sites/${siteId}/current-equipment`);
  },

  async getAppCredentials(siteId: number): Promise<SiteAppCredentialsMeta> {
    return ApiWithLogging.get<SiteAppCredentialsMeta>(`/sites/${siteId}/app-credentials`);
  },

  // POST (ไม่ใช่ GET) กันเบราว์เซอร์/proxy cache รหัสผ่าน · backend ลง activity log ทุกครั้ง
  async revealAppCredentials(siteId: number): Promise<{ site_id: number; app_login_email: string | null; password: string | null }> {
    return ApiWithLogging.post(`/sites/${siteId}/app-credentials/reveal`, {});
  },

  async updateAppCredentials(siteId: number, data: SiteAppCredentialsUpdate): Promise<SiteAppCredentialsMeta> {
    return ApiWithLogging.put<SiteAppCredentialsMeta>(`/sites/${siteId}/app-credentials`, data);
  },

  // ดึงรายการโครงการทั้งหมด
  async getSites(params: GetSitesParams = {}): Promise<GetSitesResponse> {
    try {
      // ใช้ค่าเริ่มต้นถ้าไม่มีการระบุ
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 10,
        sort: params.sort,
        order: params.order,
        search: params.search,
        company_id: params.company_id,
        status: params.status
      };
      
      console.log('Fetching sites with params:', queryParams);
      
      // ส่ง request ไปยัง API
      const response = await ApiWithLogging.get<GetSitesResponse>('/sites/', 
        { 
          params: queryParams
        },
        {
          service: 'siteService',
          action: 'view_sites_list',
          entityType: 'site'
        }
      );
      
      console.log('Fetched sites response:', response);
      return response;
    } catch (error: any) {
      console.error('Error in getSites service:', error);
      
      // Log more detailed error information if available
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  // ดึงรายการโครงการตามบริษัท
  async getSitesByCompany(company_id: number): Promise<Site[]> {
    try {
      const response = await ApiWithLogging.get<Site[]>(`/sites/company/${company_id}`, {},
        {
          service: 'siteService',
          action: 'view_sites_by_company',
          entityType: 'site',
          details: { company_id }
        }
      );
      return response;
    } catch (error) {
      console.error('Error in getSitesByCompany service:', error);
      throw error;
    }
  },

  // ดึงข้อมูลโครงการตาม id
  async getSite(id: number): Promise<Site> {
    try {
      const response = await ApiWithLogging.get<Site>(`/sites/${id}`, {},
        {
          service: 'siteService',
          action: 'view_site_detail',
          entityType: 'site',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error in getSite service:', error);
      throw error;
    }
  },

  // สร้างโครงการใหม่
  async createSite(data: SiteForm): Promise<Site> {
    try {
      // แปลงข้อมูลจาก frontend format เป็น backend format
      const siteData = {
        code: data.code,
        name: data.name,
        display_name: data.display_name || null,  // ชื่อใช้งานจริงในระบบ
        app_name: data.app_name || null,  // ชื่อที่ใช้ในแอพ
        company_id: data.company_id,
        address: data.address || '',
        // feedback amintra 3 ก.ย. 2026: payload เดิมไม่ส่ง activation_code เลย → กรอก "รหัส Activate"
        // แล้วขึ้นบันทึกสำเร็จ แต่ค่าไม่เคยถึง backend · และ "รหัสลูกค้า" ต้องลง customer_number
        // ให้ตรงกับไฟล์นำเข้า (customer_code เป็น legacy ของ External Report ไม่เขียนทับจากฟอร์ม)
        customer_number: data.customer_number || null,  // รหัสลูกค้า
        activation_code: data.activation_code || null,  // รหัส Activate สำหรับเปิดใช้งาน
        contact_person: data.contactPerson || '',
        phone: data.phone || '',
        email: data.email || null,  // ใช้ null แทน empty string สำหรับ email
        status: data.status || 'active',  // เพิ่ม status field

        // New fields for data migration
        k_connect_enabled: data.k_connect_enabled || false,
        telegram_enabled: data.telegram_enabled || false,
        project_phone: data.project_phone || null,
        contact_notes: data.contact_notes || null,

        // Site contacts
        contacts: data.contacts || []
      };

      console.log('Creating site with data:', siteData);

      const response = await ApiWithLogging.post<Site>('/sites/', siteData, {},
        {
          service: 'siteService',
          action: 'create_site',
          entityType: 'site',
          details: {
            code: data.code,
            name: data.name,
            company_id: data.company_id
          }
        }
      );
      return response;
    } catch (error: any) {
      console.error('Error in createSite service:', error);
      
      // Log more detailed error information if available
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      throw error;
    }
  },

  // แก้ไขข้อมูลโครงการ
  async updateSite(id: number, data: SiteForm, ifMatch?: string): Promise<Site> {
    try {
      // แปลงข้อมูลจาก frontend format เป็น backend format
      const siteData = {
        name: data.name,
        display_name: data.display_name || null,  // ชื่อใช้งานจริงในระบบ
        app_name: data.app_name || null,  // ชื่อที่ใช้ในแอพ
        company_id: data.company_id,
        address: data.address || '',
        // feedback amintra 3 ก.ย. 2026: payload เดิมไม่ส่ง activation_code เลย → กรอก "รหัส Activate"
        // แล้วขึ้นบันทึกสำเร็จ แต่ค่าไม่เคยถึง backend · และ "รหัสลูกค้า" ต้องลง customer_number
        // ให้ตรงกับไฟล์นำเข้า (customer_code เป็น legacy ของ External Report ไม่เขียนทับจากฟอร์ม)
        customer_number: data.customer_number || null,  // รหัสลูกค้า
        activation_code: data.activation_code || null,  // รหัส Activate สำหรับเปิดใช้งาน
        contact_person: data.contactPerson || '',
        phone: data.phone || '',
        email: data.email || null,  // ใช้ null แทน empty string สำหรับ email
        status: data.status || 'active',  // เพิ่ม status field

        // New fields for data migration
        k_connect_enabled: data.k_connect_enabled,
        telegram_enabled: data.telegram_enabled,
        project_phone: data.project_phone || null,
        contact_notes: data.contact_notes || null,

        // Site contacts (if provided)
        contacts: data.contacts,

        // E2E v6 OBS-3: flag ยืนยันปิดโครงการ — ส่งเฉพาะเมื่อ true (backend ไม่เขียนลง DB)
        ...(data.confirm_deactivate ? { confirm_deactivate: true } : {})
      };

      console.log(`Updating site ID ${id} with data:`, siteData);

      const response = await ApiWithLogging.put<Site>(`/sites/${id}`, siteData, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
        {
          service: 'siteService',
          action: 'update_site',
          entityType: 'site',
          entityId: id,
          details: {
            name: data.name,
            company_id: data.company_id
          }
        }
      );
      return response;
    } catch (error) {
      console.error('Error in updateSite service:', error);
      throw error;
    }
  },

  // ตรวจสอบรหัสซ้ำ
  async checkCodeExists(code: string): Promise<boolean> {
    try {
      const response = await ApiWithLogging.get<{ items: any[]; total: number }>('/sites/', 
        { params: { search: code, limit: 1000 } },
        {
          service: 'siteService',
          action: 'check_site_code',
          entityType: 'site',
          details: { code }
        }
      );
            // KD3 S2126 (BUG-S2126-001): GET /sites/ ไม่มีพารามิเตอร์ code (ถูกทิ้ง = ตอบซ้ำทุกครั้ง) → เทียบรหัสตรงตัวเอง
      // (ไม่สนตัวพิมพ์ตาม collation ของ DB) · ดึงหลายแถวไม่งั้นแถวที่ตรงจริงอาจไม่อยู่หน้าแรก
      return isExactCodeMatch(response.items.map(x => x.code), code);
    } catch (error) {
      console.error('Error checking site code:', error);
      return false;
    }
  },

  // ลบโครงการ
  async deleteSite(id: number): Promise<any> {
    try {
      console.log(`Deleting site with ID: ${id}`);
      
      const response = await ApiWithLogging.delete(`/sites/${id}`, {},
        {
          service: 'siteService',
          action: 'delete_site',
          entityType: 'site',
          entityId: id
        }
      );
      // ApiWithLogging unwraps .data already — response IS the body directly
      const body: any = response;
      console.log(`Delete site API response:`, body);

      // ตรวจสอบผลการลบจาก response
      if (body && body.success === false) {
        // API ส่งสถานะสำเร็จแต่ไม่สามารถลบโครงการได้
        console.warn('Cannot delete site:', body.message);

        // สร้าง Error ด้วยข้อความจาก API
        const error = new Error(body.message || 'ไม่สามารถลบโครงการได้');
        // เพิ่มข้อมูลเพิ่มเติมเกี่ยวกับโครงการและความสัมพันธ์
        (error as any).siteData = body.site;
        (error as any).relatedInfo = body.related_info;
        throw error;
      }

      // ถ้าลบสำเร็จ
      return body;
    } catch (error: any) {
      console.error('Error in deleteSite service:', error);
      
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
        
        if (error.response.data) {
          if (error.response.data.message) {
            error.message = error.response.data.message;
            
            if (error.response.data.site) {
              (error as any).siteData = error.response.data.site;
            }
            if (error.response.data.related_info) {
              (error as any).relatedInfo = error.response.data.related_info;
            }
          }
          else if (error.response.data.detail) {
            if (typeof error.response.data.detail === 'string') {
              error.message = error.response.data.detail;
            }
          }
        }
      }
      
      if (error.message && typeof error.message === 'string') {
        if (error.message.includes('Request failed with status code')) {
          error.message = 'ไม่สามารถลบโครงการได้เนื่องจากเกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์';
        } else if (error.message.includes('pymysql.err.OperationalError')) {
          error.message = 'ไม่สามารถลบโครงการได้เนื่องจากเกิดข้อผิดพลาดในฐานข้อมูล';
          error.isSqlError = true;
        }
      }
      
      throw error;
    }
  },

  // ส่งออกข้อมูลโครงการเป็น Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย
  // ไม่งั้นผู้ใช้ค้นหา/กรองแล้วกดส่งออก จะได้ไฟล์ทั้งระบบโดยไม่มีอะไรเตือน
  async exportToExcel(params?: { search?: string; company_id?: string | number; status?: string }): Promise<Blob> {
    const query: Record<string, string> = {};
    if (params?.search) query.search = params.search;
    if (params?.company_id) query.company_id = String(params.company_id);
    if (params?.status) query.status = params.status;

    const response = await ApiWithLogging.get('/sites/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'siteService',
        action: 'export_sites_excel',
        entityType: 'site'
      }
    );
    return response;
  }
};

export default siteService;